<template>
  <styled-multiselect
    v-model="selectedConfig"
    :disabled="disabled"
    :label="label"
    :loading="isFetchingConfigs"
    :options="checkBotConfigs"
    :placeholder="$tt('checkBotConfigSelect.selectConfig')"
    :track-by="trackBy"
  ></styled-multiselect>
</template>

<script lang="ts">
  import { ICheckBotConfigSummaryDto } from 'types';
  import StyledMultiselect from '@/components/StyledMultiselect/StyledMultiselect.vue';
  import { CheckBotConfigsService } from '@/services';
  import { defineComponent } from 'vue';

  export default defineComponent({
    name: 'CheckBotConfigSelect',
    components: { StyledMultiselect },
    props: {
      disabled: {
        type: Boolean,
        default: false,
      },
      isWaitingForSupplier: {
        type: Boolean,
        default: false,
      },
      projectId: {
        type: String,
        required: true,
      },
      supplierId: {
        type: String as () => string | undefined,
        required: false,
      },
      modelValue: {
        type: Object as () => ICheckBotConfigSummaryDto | null,
        default: null,
      },
    },
    emits: ['update:modelValue'],
    data(): {
      checkBotConfigs: ICheckBotConfigSummaryDto[];
      isFetchingConfigs: boolean;
      selectedConfig: ICheckBotConfigSummaryDto | null;
    } {
      return {
        checkBotConfigs: [],
        isFetchingConfigs: false,
        selectedConfig: null,
      };
    },
    computed: {
      label(): keyof ICheckBotConfigSummaryDto {
        return 'name';
      },
      trackBy(): keyof ICheckBotConfigSummaryDto {
        return 'name';
      },
    },
    watch: {
      modelValue(newValue) {
        this.selectedConfig = newValue;
      },
      selectedConfig(newValue) {
        this.$emit('update:modelValue', newValue);
      },
      isWaitingForSupplier(isWaiting: boolean) {
        if (isWaiting) {
          this.selectedConfig = null;
        } else this.fetchCheckBotConfigs();
      },
      supplierId() {
        this.fetchCheckBotConfigs();
      },
    },
    mounted() {
      this.fetchCheckBotConfigs();
    },
    methods: {
      async fetchCheckBotConfigs(): Promise<void> {
        if (this.isWaitingForSupplier) {
          return;
        }
        this.isFetchingConfigs = true;
        try {
          const configsDto = await CheckBotConfigsService.getCheckBotConfigs(
            this.projectId,
            this.supplierId,
          );
          this.checkBotConfigs = configsDto.configs;
        } catch {
          this.createErrorToast(
            this.$tt('checkBotConfigSelect.errors.errorGettingCheckBotConfigs'),
          );
        } finally {
          this.isFetchingConfigs = false;
        }
      },
    },
  });
</script>
